import { IUserContext, FeatureFlagsType, IFeatureFlagState } from '../../common/interfaces/featureFlags';
import { getItem, setItem, removeItem } from '../storage';

const FEATURE_FLAGS_STORAGE_KEY = 'featureFlags';

const getCurrentUserId = (): string | null => {
  const userItem = getItem('user');
  if (!userItem?.user?.id) {
    return null;
  }
  return String(userItem.user.id);
};

const isStoredFlagsForCurrentUser = (): boolean => {
  const featureFlagsState = getItem(FEATURE_FLAGS_STORAGE_KEY);
  const storedUserId = featureFlagsState?.userContext?.userId;
  if (!storedUserId) {
    return true;
  }
  const currentUserId = getCurrentUserId();
  if (!currentUserId) {
    return false;
  }
  return storedUserId === currentUserId;
};

/**
 * Save feature flags to localStorage
 */
export const setFeatureFlagsFromApi = (flags: FeatureFlagsType, userContext: IUserContext) => {
  const state: IFeatureFlagState = { flags, userContext };
  setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(state));
};

/**
 * Get all feature flags from localStorage
 */
export const getAllFlags = (): FeatureFlagsType => {
  if (!isStoredFlagsForCurrentUser()) {
    return {};
  }

  const featureFlagsState = getItem(FEATURE_FLAGS_STORAGE_KEY);
  if (!featureFlagsState || !featureFlagsState.flags) {
    return {};
  }

  return featureFlagsState.flags;
};

/**
 * Get a boolean feature flag. JSON flags such as `blockoutFeature` are
 * omitted here; read those from `getAllFlags()`.
 */
export const getFlag = (flagName: string): boolean | undefined => {
  const value = getAllFlags()[flagName];
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * Get the user context from localStorage
 */
export const getUserContext = (): IUserContext | null => {
  const featureFlagsState = getItem(FEATURE_FLAGS_STORAGE_KEY);
  if (!featureFlagsState || !featureFlagsState.userContext) {
    return null;
  }
  return featureFlagsState.userContext;
};

/**
 * Clear feature flags from localStorage.
 * Call this on logout to ensure flags are refreshed for the next user.
 */
export const clearFeatureFlags = () => {
  removeItem(FEATURE_FLAGS_STORAGE_KEY);
};
